
export function getRotatedPolygonsFeature(cLat, cLon, hwLat, hwLon, pLat = 90, pLon = -180, altitude, material, color) {

    return {
        "type": "Feature",
        "properties": {},
        "geometry": {
            "type": "Polygon",
            "coordinates": [
                calculateRotatedDomainPolygon(cLat, cLon, hwLat, hwLon, pLat, pLon)
            ]
        },
        "altitude": altitude,
        "material": material,
        "color": color,
    }
}

export function calculateRotatedDomainPolygon(cLat, cLon, hwLat, hwLon, pLat = 90, pLon = -180, reverseOrderToLatLon = false, yOverflow = false) {
    const N = 150; // outline accuracy (no of polygons per side)
    const pi_180 = 4. * Math.atan(1.) / 180;

    let xx = new Array(4 * N + 1);
    let yy = new Array(4 * N + 1);
    const c = [cLat, cLon]

    for (let i = 0; i < N; i++) {
        let x = 2. * (i / N - 0.5);
        let j = i;
        xx[j] = c[1] + x * hwLon;
        yy[j] = c[0] - 1.0 * hwLat;
        j = j + N;
        xx[j] = c[1] + 1.0 * hwLon;
        yy[j] = c[0] + x * hwLat;
        j = j + N;
        xx[j] = c[1] + (0. - x) * hwLon;
        yy[j] = c[0] + 1.0 * hwLat;
        j = j + N;
        xx[j] = c[1] + -1.0 * hwLon;
        yy[j] = c[0] + (0. - x) * hwLat;
    }
    xx[4 * N] = xx[0]; // close polyline
    yy[4 * N] = yy[0];

    // react-globe requires the polygon points to be defined in counterclockwise order, so we need to reverse the arrays
    xx = xx.reverse()
    yy = yy.reverse()

    // step 2: create rotated coordinates
    let lambda = xx;
    let phi = yy;
    for (let i = 0; i < 4 * N; i++) {
        lambda[i] *= pi_180;
        phi[i] *= pi_180;
    }
    let pole_lambda = pLon * pi_180;
    let pole_phi = pLat * pi_180;

    let rot_phi = new Array(4 * N);
    let rot_lambda = new Array(4 * N);

    for (let i = 0; i < 4 * N; i++) {
        const rotatedCoords = calculateRotatedCoordinates(phi[i], lambda[i], pole_phi, pole_lambda)

        rot_phi[i] = rotatedCoords.latRot;
        rot_lambda[i] = rotatedCoords.lonRot;
    }


    // yOverflow: if the map goes beyond lon 180 to a new map, such as with the leaflet map
    if (yOverflow) {
        for (let i = 1; i < 4 * N; i++) {
            if ((rot_lambda[i] * rot_lambda[i - 1] < 0.) && (Math.abs(rot_lambda[i] / pi_180) > 10.))
                rot_lambda[i] += Math.sign(rot_lambda[i - 1]) * 360. * pi_180;
        }
    }

    // step 3: construct polygon with rotated coordinates
    let rotated_coords = [];
    for (let i = 0; i < 4 * N; i++) {
        if (reverseOrderToLatLon) {
            rotated_coords.push([radToDeg(rot_phi[i]), radToDeg(rot_lambda[i])])
        } else {
            rotated_coords.push([radToDeg(rot_lambda[i]), radToDeg(rot_phi[i])])
        }
    }
    rotated_coords.push(rotated_coords[0]) // close polyline

    return rotated_coords;
}

const ZERO_TOL = 1.e-15;

export function calculateRotatedCoordinates(lat, lon, poleLat = 90, poleLon = -180, deg = false) {
    if (deg) {
        lat = degToRad(lat);
        lon = degToRad(lon);
        poleLat = degToRad(poleLat);
        poleLon = degToRad(poleLon);
    }
    let arg1 = Math.cos(lat) * Math.sin(lon);
    let arg2 = Math.sin(poleLat) * Math.cos(lat) * Math.cos(lon) - Math.sin(lat) * Math.cos(poleLat);

    let lonRot;
    if ((Math.abs(arg1) > ZERO_TOL) || (Math.abs(arg2) > ZERO_TOL))
        lonRot = Math.atan2(-1. * Math.sin(poleLon) * arg2 - Math.cos(poleLon) * arg1,
            -1. * Math.cos(poleLon) * arg2 + Math.sin(poleLon) * arg1);
    else
        lonRot = 0.0; // ATAN2(0,0) is undefined, so we just have to set something

    let latRot =
        Math.asin(Math.sin(lat) * Math.sin(poleLat) +
            Math.cos(lat) * Math.cos(lon) * Math.cos(poleLat));

    return {
        latRot: deg ? radToDeg(latRot) : latRot,
        lonRot: deg ? radToDeg(lonRot) : lonRot,
    }
}

function radToDeg(val) {
    return val * 180 / Math.PI;
}

function degToRad(val) {
    return val * Math.PI / 180;
}

export function calculateEstimations(n, k, domainIndex, domains) {
    const R_Earth = 6371; // Earth radius in km
    let resolution = (R_Earth * Math.sqrt(Math.PI / 5)) / (n * Math.pow(2, k));
    const domain = domains[domainIndex];

    /*
        a nested domain should have half the resolution of its parent domain
        to calculate this we simulate an array with only parents of the domain by removing any duplicate IDs
        then we can use non_nested_domain_resolution/ 2^length_of_simulated_array
     */
    if (domain.icontools.parent_id > 0) {
        // only include IDs up to the domain
        const parentIdArray = Array.from(domains.map((domain) => domain.icontools.parent_id))
        const relevantArray = parentIdArray.splice(1, parentIdArray.indexOf(domain.icontools.parent_id))
        const uniqueRelevantArray = [...new Set(relevantArray)]
        const factor = uniqueRelevantArray.length;
        resolution = resolution / Math.pow(2, factor);
    }

    let domainSurface;
    if (domain.icontools.region_type === 1) {
        domainSurface = 4 * Math.PI * Math.pow(R_Earth, 2);
    } else if (domain.icontools.region_type === 2) {
        domainSurface = 2 * Math.PI * Math.pow(R_Earth, 2) * (1 - Math.cos(degToRad(domain.icontools.radius)));
        console.log(domainSurface)
    } else {
        const lat1Rad = ((domain.icontools.center_lat - domain.icontools.hwidth_lat) / 180) * Math.PI;
        const lat2Rad = ((parseFloat(domain.icontools.center_lat) + parseFloat(domain.icontools.hwidth_lat)) / 180) * Math.PI;
        domainSurface =
            Math.PI *
            Math.pow(R_Earth, 2) *
            Math.abs(Math.sin(lat1Rad) - Math.sin(lat2Rad)) *
            (domain.icontools.hwidth_lon / 90);
    }
    const cellSurface = Math.pow(resolution, 2);
    const triangles = Math.round(domainSurface / cellSurface);

    return {
        resolution: resolution,
        triangles: triangles
    }
}

// scalar-matrix multiply (3x3)
function multiply_scalar_matrix(c, A) {
    const C = A;
    for (let i = 0; i < 9; i++)
        C[i] = c * C[i];
    return C;
}

// matrix-matrix multiply (3x3)
function multiply_matrix_matrix(A, B) {
    const N = Math.round(B.length / 3);
    const C = new Array(3 * N);
    for (let i = 0; i < 3; i++)
        for (let j = 0; j < N; j++) {
            let value = 0.0;
            for (let k = 0; k < 3; k++)
                value += A[i + 3 * k] * B[k + 3 * j];
            C[i + 3 * j] = value;
        }
    return C;
}

// matrix-matrix add (3x3)
function add_matrix(A, B) {
    const N = 3;
    const C = new Array(3 * N);
    for (let i = 0; i < 3; i++)
        for (let j = 0; j < N; j++) {
            C[i + 3 * j] = A[i + 3 * j] + B[i + 3 * j];
        }
    return C;
}

// compute rotation matrix from axis and angle (in degrees)
function rotation_matrix(u, theta) {
    // identity matrix
    const I = [1.0, 0.0, 0.0,
        0.0, 1.0, 0.0,
        0.0, 0.0, 1.0];
    // cross prod. matrix
    const Ux = [0.0, u[2], -1.0 * u[1],
        -1.0 * u[2], 0.0, u[0],
        u[1], -1.0 * u[0], 0.0];
    // tensor product
    const UxU = [];
    for (let i = 0; i < 3; i++)
        for (let j = 0; j < 3; j++)
            UxU[i + 3 * j] = u[i] * u[j];

    return add_matrix(add_matrix(multiply_scalar_matrix(Math.cos(theta), I),
            multiply_scalar_matrix(Math.sin(theta), Ux)),
        multiply_scalar_matrix(1.0 - Math.cos(theta), UxU));
}


// transformation into cartesian coordinates:
function gc2cc(lon, lat) {
    const pi_180 = 4. * Math.atan(1.) / 180;
    return [Math.cos(lon * pi_180) * Math.cos(lat * pi_180),
        Math.sin(lon * pi_180) * Math.cos(lat * pi_180),
        Math.sin(lat * pi_180)];
}

// transformation into geographical coordinates:
function cc2gc(p_x) {
    const pi_180 = 4. * Math.atan(1.) / 180;
    const pi_2 = 2. * Math.atan(1.);
    const dbl_eps = 1.e-12
    const z_r = Math.sqrt(p_x[0] * p_x[0] + p_x[1] * p_x[1])
    let p_pos = [0., 0., 0.];
    if (Math.abs(z_r) < dbl_eps) {    // one of the poles
        if (p_x[2] > 0.0)
            p_pos[1] = pi_2;
        else
            p_pos[1] = -1. * pi_2;
        p_pos[0] = 0.;
    }
    else {
        p_pos[1] = Math.atan2(p_x[2], z_r);
        if (Math.abs(p_x[0]) < dbl_eps) {   // p_x[0] == 0 ?
            if (p_x[1] >= 0.0)
                p_pos[0] = pi_2;
            else
                p_pos[0] = -1. * pi_2;
        }
        else
            p_pos[0] = Math.atan2(p_x[1], p_x[0]);
    }
    p_pos[0] /= pi_180;
    p_pos[1] /= pi_180;
    return p_pos;
}

export function calculateCircularDomainPolygons(centerLat, centerLon, radius, reverseOrderToLatLon = false, yOverflow = false) {
    const outline_accuracy = 150;
    const pi_180 = 4. * Math.atan(1.) / 180;
    const c = [centerLat, centerLon]
    const path_obj = [];
    // first, rotate the center point around the axis "axis":
    const pc = [centerLat + 90.0, centerLon]
    if (pc[0] > 90.0) {
        pc[0] = 180.0 - pc[0];
        pc[1] += 180.0;
        if (pc[1] > 180.0) {
            pc[1] -= 360.0;
        }
    }
    const cc = gc2cc(c[1], c[0]);
    let axis = gc2cc(pc[1], pc[0]);
    const R0 = rotation_matrix(axis, radius * pi_180);
    const p_rot0 = multiply_matrix_matrix(R0, cc);

    // now, rotate the p_rot0 around the axis "center_point"
    let dx = 360. / outline_accuracy
    for (let angle = 0.0; angle <= 360.0; angle += dx) {
        const R = rotation_matrix(cc, angle * pi_180);
        const p_rot = multiply_matrix_matrix(R, p_rot0);
        const p_gc = cc2gc(p_rot)
        if (reverseOrderToLatLon) {
            path_obj.push([p_gc[1], p_gc[0]]);
        } else {
            path_obj.push([p_gc[0], p_gc[1]]);
        }
    }
    path_obj.push(path_obj[0]);


    // yOverflow: if the map goes beyond lon 180 to a new map, such as with the leaflet map
    if (yOverflow) {
        for (let i = 1; i < path_obj.length; i++) {
            if (centerLon + radius > 180 && path_obj[i][1] < 0) {
                path_obj[i][1] += 360;
            } else if (centerLon - radius < -180 && path_obj[i][1] > 0) {
                path_obj[i][1] += -360;
            }
        }
    }

    // react-globe requires the polygon points to be defined in counterclockwise order, so we need to reverse the array
    return path_obj.reverse()
}


export function getRotatedCircularPolygonsFeature(centerLat, centerLon, radius, altitude, material, color) {

    return {
        "type": "Feature",
        "properties": {},
        "geometry": {
            "type": "Polygon",
            "coordinates": [
                calculateCircularDomainPolygons(centerLat, centerLon, radius)
            ]
        },
        "altitude": altitude,
        "material": material,
        "color": color,
    }
}